// src/pages/MyCirclesPage.jsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Copy, Check, ArrowLeft, ArrowRight, UserCircle } from 'lucide-react';
import { useCirclesStore } from '../stores/circlesStore';
import { usePrivacyAuth } from '../context/PrivacyAuthContext';

export default function MyCirclesPage({ onBack, onCircleSelect }) {
  const { myCircles, activeCircleId, setActiveCircle } = useCirclesStore();
  const { user } = usePrivacyAuth();
  const [copiedCode, setCopiedCode] = useState(null);

  // Helper function to format Firestore Timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      // Handle Firestore Timestamp object
      if (timestamp?.toDate) {
        return timestamp.toDate().toLocaleDateString();
      }
      // Handle JavaScript Date object
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString();
      }
      // Handle timestamp number or string
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const handleCopyCode = (inviteCode, circleId) => {
    navigator.clipboard.writeText(inviteCode);
    setCopiedCode(circleId);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSwitchCircle = (circleId) => {
    setActiveCircle(circleId);
    if (onCircleSelect) {
      onCircleSelect(circleId);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <div className="text-center text-white">
          <p className="text-xl mb-4">Please log in to view your circles</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/50 border-b border-purple-500/30 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-purple-400" />
            My Circles
          </h1>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {myCircles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 rounded-2xl p-8 border border-purple-500/30 text-center"
          >
            <Users className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Circles Yet</h2>
            <p className="text-purple-200 mb-6">
              You haven't created or joined any Friend Circles yet.
            </p>
            <button
              onClick={onBack}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Get Started
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <p className="text-purple-300 text-sm mb-6">
              You&apos;re in {myCircles.length} {myCircles.length === 1 ? 'circle' : 'circles'}.
              Switch between them here.
            </p>

            {myCircles.map((circle, index) => {
              const isActive = circle.id === activeCircleId;
              const memberCount = circle.members?.length || 0;

              return (
                <motion.div
                  key={circle.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative backdrop-blur-sm rounded-2xl p-6 border transition-all ${
                    isActive
                      ? 'bg-gradient-to-br from-purple-600/40 to-pink-600/40 border-purple-300 shadow-2xl shadow-purple-500/30 ring-2 ring-purple-400/50'
                      : 'bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-500/30 hover:border-purple-400/50'
                  }`}
                >
                  {/* Active Badge - Large & Prominent */}
                  {isActive && (
                    <div className="absolute -top-3 -right-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      Currently Viewing
                    </div>
                  )}

                  {/* Circle Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-4xl">{circle.emoji}</span>
                        <div>
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            {circle.name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-purple-300">
                            <UserCircle className="w-4 h-4" />
                            <span>{memberCount} {memberCount === 1 ? 'person' : 'people'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!isActive && (
                      <button
                        onClick={() => handleSwitchCircle(circle.id)}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm"
                      >
                        Switch
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Invite Code Section */}
                  <div className="bg-white/5 rounded-xl p-4 border border-purple-400/20">
                    <p className="text-purple-200 text-xs font-medium mb-2">
                      Invite Code:
                    </p>
                    <div className="flex gap-3">
                      <div className="flex-1 bg-slate-800/50 rounded-lg px-4 py-2.5 font-mono text-xl text-white border border-purple-500/20">
                        {circle.inviteCode}
                      </div>
                      <button
                        onClick={() => handleCopyCode(circle.inviteCode, circle.id)}
                        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center gap-2 text-sm"
                      >
                        {copiedCode === circle.id ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-xs text-purple-300 mt-2">
                      Send it privately to friends you know. Anyone with the code can join, so if it gets out,
                      open the circle and get a new one.
                    </p>
                  </div>

                  {/* Circle Stats */}
                  <div className="mt-4 flex gap-4 text-sm">
                    <div className="flex-1 bg-white/5 rounded-lg p-3 text-center">
                      <p className="text-purple-300 text-xs mb-1">Created</p>
                      <p className="text-white font-semibold">
                        {formatDate(circle.createdAt)}
                      </p>
                    </div>
                    <div className="flex-1 bg-white/5 rounded-lg p-3 text-center">
                      <p className="text-purple-300 text-xs mb-1">Last Active</p>
                      <p className="text-white font-semibold">
                        {formatDate(circle.lastActivityAt)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-blue-900/20 rounded-xl p-6 border border-blue-500/30"
        >
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            How to use Friend Circles
          </h3>
          <ul className="space-y-2 text-sm text-blue-200">
            <li className="flex gap-2">
              <span className="text-blue-400">•</span>
              <span>
                <strong>Switch circles:</strong> Press Switch to open a different circle
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-400">•</span>
              <span>
                <strong>Invite friends:</strong> Send the code privately to friends you know, never anywhere public
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-400">•</span>
              <span>
                <strong>Learn together:</strong> Your circle sees whether you&apos;ve finished the module it&apos;s on, and your answers to its questions once they reveal. Nothing you write in a module is shared
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-400">•</span>
              <span>
                <strong>Leaving:</strong> Anyone can leave a circle at any time, from the circle&apos;s page. Nobody can remove anyone else
              </span>
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
